import { TrendingUp } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { Forecast } from '@/features/dashboard/lib/forecast'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/features/reports/lib/format'

/**
 * The running day's end-of-day estimate (CHG-2026-09-manager-reporting, D13).
 *
 * Rendered as an estimate and nothing else: a `≈`, the typical comparable day's figure beside it,
 * and never the visual weight of a measured number. With no comparable day it says so rather than
 * extrapolating from nothing — a dashboard that guesses quietly is worse than one that admits it
 * cannot.
 */
export function ForecastTile({
  forecast,
  loading,
}: {
  forecast: Forecast
  loading?: boolean
}) {
  const { t } = useTranslation('reports')

  return (
    <Card>
      <CardContent className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
          {t('forecast.label')}
        </div>
        {loading ? (
          <Skeleton className="h-9 w-28" />
        ) : forecast.endOfDayCents == null ? (
          <div className="text-xl font-medium leading-9 text-muted-foreground">
            {t('forecast.unavailable')}
          </div>
        ) : (
          <div className="text-3xl font-semibold leading-9 tracking-tight">
            <span aria-hidden="true">≈ </span>
            <span className="sr-only">{t('forecast.estimatePrefix')} </span>
            {formatCurrency(forecast.endOfDayCents)}
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          {forecast.typicalDayCents != null
            ? t('forecast.typicalDay', {
                value: formatCurrency(forecast.typicalDayCents),
                days: forecast.referenceDays,
              })
            : t('forecast.noComparableDay')}
        </div>
      </CardContent>
    </Card>
  )
}
